import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  input,
  Output,
  signal,
} from '@angular/core';

@Directive({
  selector: '[shrlSwipe]',
  standalone: true,
})
export class SwipeDirective {
  readonly #touchStartX = signal<number | null>(null);
  readonly #touchStartY = signal<number | null>(null);
  readonly #isDragging = signal(false);

  readonly minSwipeDistance = input<number>(50);
  readonly minSwipeDistancePercent = input<number>(0.2);

  @Output() swipeLeft = new EventEmitter<void>();
  @Output() swipeRight = new EventEmitter<void>();

  constructor(private el: ElementRef<HTMLElement>) {}

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    const touch = event.touches[0];
    this.#touchStartX.set(touch.clientX);
    this.#touchStartY.set(touch.clientY);
    this.#isDragging.set(true);
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.#isDragging() || !this.#touchStartX() || !this.#touchStartY()) {
      return;
    }
    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.#touchStartX()!);
    const deltaY = Math.abs(touch.clientY - this.#touchStartY()!);
    // Only prevent default for horizontal swipes
    if (deltaX > deltaY) {
      event.preventDefault();
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.#isDragging() || !this.#touchStartX() || !this.#touchStartY()) {
      return;
    }

    const touch = event.changedTouches[0];
    const startX = this.#touchStartX()!;
    const startY = this.#touchStartY()!;
    const endX = touch.clientX;
    const endY = touch.clientY;

    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Calculate minimum swipe distance
    const container = this.el.nativeElement;
    const minDistance = Math.min(
      this.minSwipeDistance(),
      container.offsetWidth * this.minSwipeDistancePercent()
    );

    // Only trigger swipe if horizontal movement is greater than vertical
    // and exceeds the minimum swipe threshold
    if (absDeltaX > absDeltaY && absDeltaX > minDistance) {
      if (deltaX > 0) {
        // Swipe right
        this.swipeRight.emit();
      } else {
        // Swipe left
        this.swipeLeft.emit();
      }
    }

    this.#touchStartX.set(null);
    this.#touchStartY.set(null);
    this.#isDragging.set(false);
  }
}
